// src/api/client.js
// Cliente HTTP global y centralizado.
// REGLA: `fetch` no debe usarse directamente fuera de este archivo.

const BASE_URL = (process.env.REACT_APP_API_URL || '') + '/api';

// Error tipado para respuestas HTTP no-OK
export class ApiError extends Error {
  constructor(status, message, data) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

// Función base de petición
async function request(url, options = {}) {

  const token = sessionStorage.getItem('token');

  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const res = await fetch(`${BASE_URL}${url}`, { ...options, headers });

  if (res.status === 401) {
    const isPublicFlow = url.includes('/auth/login') ||
                         url.includes('/auth/forgot-password') ||
                         url.includes('/auth/reset-password') ||
                         url.includes('/auth/validate-reset-token') ||
                         url.includes('/onboarding');
    if (!isPublicFlow) {
      sessionStorage.clear();
      window.location.href = '/';
    }
    throw new ApiError(401, url.includes('/auth/login') ? 'Credenciales inválidas' : 'No autorizado o sesión expirada.');
  }

  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}));
    const message =
      errorBody.message || errorBody.error || `Error del servidor (${res.status})`;
    throw new ApiError(res.status, message, errorBody);
  }

  // Manejar respuestas sin cuerpo (204 o 200 sin contenido)
  if (res.status === 204) return null;
  const text = await res.text();
  return text ? JSON.parse(text) : null;
}

// Peticiones multipart/form-data 
async function requestFormData(url, formData, options = {}) {
  const token = sessionStorage.getItem('token');

  const headers = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const res = await fetch(`${BASE_URL}${url}`, {
    method: 'POST',
    ...options,
    headers,
    body: formData,
  });

  if (res.status === 401) {
    sessionStorage.clear();
    window.location.href = '/';
    throw new ApiError(401, 'Sesión expirada.');
  }

  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}));
    const message =
      errorBody.message || errorBody.error || `Error del servidor (${res.status})`;
    throw new ApiError(res.status, message, errorBody);
  }

  if (res.status === 204) return null;
  const text = await res.text();
  return text ? JSON.parse(text) : null;
}

// Peticiones que retornan Blob (PDFs, archivos descargables)
async function requestBlob(url, options = {}) {
  const token = sessionStorage.getItem('token');

  const headers = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const res = await fetch(`${BASE_URL}${url}`, { ...options, headers });

  if (res.status === 401) {
    sessionStorage.clear();
    window.location.href = '/';
    throw new ApiError(401, 'Sesión expirada.');
  }

  if (!res.ok) {
    throw new ApiError(res.status, `Error al descargar archivo (${res.status})`);
  }

  return res.blob();
}

// Peticiones que retornan el objeto Response crudo
async function requestRaw(url, options = {}) {
  const token = sessionStorage.getItem('token');

  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const res = await fetch(`${BASE_URL}${url}`, { ...options, headers });

  if (res.status === 401) {
    if (!url.includes('/auth/login')) {
      sessionStorage.clear();
      window.location.href = '/';
    }
    throw new ApiError(401, 'Sesión expirada.');
  }

  return res;
}

// API pública del cliente
export const api = {
  get: (url, opts) => request(url, { method: 'GET', ...opts }),
  post: (url, body, opts) => request(url, { method: 'POST', body: JSON.stringify(body), ...opts }),
  put: (url, body, opts) => request(url, { method: 'PUT', body: JSON.stringify(body), ...opts }),
  patch: (url, body, opts) => request(url, { method: 'PATCH', body: JSON.stringify(body), ...opts }),
  delete: (url, opts) => request(url, { method: 'DELETE', ...opts }),
  postForm: (url, formData, opts) => requestFormData(url, formData, opts),
  getBlob: (url, opts) => requestBlob(url, { method: 'GET', ...opts }),
  postBlob: (url, body, opts) => requestBlob(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), ...opts }),
  getRaw: (url, opts) => requestRaw(url, { method: 'GET', ...opts }),
  postRaw: (url, body, opts) => requestRaw(url, { method: 'POST', body: JSON.stringify(body), ...opts }),
};
